// Admin Panel Token Manager Service
// Handles JWT token storage, refresh, and lifecycle

class TokenManager {
  static STORAGE_KEY = 'adminAuth';
  static REFRESH_THRESHOLD = 60 * 1000; // Refresh if <1min remaining
  static CLEANUP_INTERVAL = 5 * 60 * 1000; // Check every 5 minutes
  static refreshTimer = null;

  /**
   * Initialize token manager
   * Call this once when app starts (in App.js or index.js)
   */
  static init() {
    this.ensureValidToken();
    
    // Set up periodic token refresh check
    this.stopRefreshTimer();
    this.refreshTimer = setInterval(() => {
      this.ensureValidToken();
    }, this.CLEANUP_INTERVAL);

    // Listen for storage changes (logout from other tabs)
    window.addEventListener('storage', (e) => {
      if (e.key === this.STORAGE_KEY && !e.newValue) {
        // Token was cleared in another tab
        this.clear();
      }
    });
  }

  /**
   * Stop periodic refresh timer
   */
  static stopRefreshTimer() {
    if (this.refreshTimer) {
      clearInterval(this.refreshTimer);
      this.refreshTimer = null;
    }
  }

  /**
   * Store tokens in localStorage
   */
  static setTokens(accessToken, refreshToken) {
    const auth = {
      accessToken,
      refreshToken,
      storedAt: Date.now()
    };
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(auth));
  }

  /**
   * Get current tokens
   */
  static getTokens() {
    const data = localStorage.getItem(this.STORAGE_KEY);
    if (!data) {
      return { accessToken: null, refreshToken: null };
    }

    try {
      const auth = JSON.parse(data);
      return {
        accessToken: auth.accessToken,
        refreshToken: auth.refreshToken
      };
    } catch (error) {
      console.error('Error parsing stored tokens:', error);
      return { accessToken: null, refreshToken: null };
    }
  }

  /**
   * Get access token
   */
  static getAccessToken() {
    const { accessToken } = this.getTokens();
    return accessToken;
  }

  /**
   * Get refresh token
   */
  static getRefreshToken() {
    const { refreshToken } = this.getTokens();
    return refreshToken;
  }

  /**
   * Decode JWT without verification (client-side only)
   */
  static decodeToken(token) {
    if (!token) return null;

    try {
      const parts = token.split('.');
      if (parts.length !== 3) return null;

      const decoded = JSON.parse(
        atob(parts[1])
      );
      return decoded;
    } catch (error) {
      console.error('Error decoding token:', error);
      return null;
    }
  }

  /**
   * Check if token is expired
   */
  static isTokenExpired(token) {
    const decoded = this.decodeToken(token);
    if (!decoded || !decoded.exp) return true;

    const expiresAt = decoded.exp * 1000; // Convert to milliseconds
    return Date.now() >= expiresAt;
  }

  /**
   * Get remaining time in seconds
   */
  static getTokenTtl(token) {
    const decoded = this.decodeToken(token);
    if (!decoded || !decoded.exp) return -1;

    return Math.floor(decoded.exp - Date.now() / 1000);
  }

  /**
   * Check if we should refresh token
   * Returns true if token expires within REFRESH_THRESHOLD
   */
  static shouldRefreshToken(token) {
    const ttl = this.getTokenTtl(token);
    return ttl > 0 && ttl * 1000 < this.REFRESH_THRESHOLD;
  }

  /**
   * Attempt to refresh access token
   */
  static async refreshAccessToken(apiBaseUrl) {
    const { refreshToken } = this.getTokens();
    
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    try {
      const response = await fetch(`${apiBaseUrl}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.getAccessToken()}`
        },
        body: JSON.stringify({ refreshToken })
      });

      if (!response.ok) {
        if (response.status === 401) {
          // Refresh token is also invalid - clear and redirect to login
          this.clear();
          window.location.href = '/login';
          throw new Error('Session expired. Please login again.');
        }
        throw new Error(`Token refresh failed: ${response.statusText}`);
      }

      const data = await response.json();
      this.setTokens(data.accessToken, data.refreshToken);
      
      return data.accessToken;
    } catch (error) {
      console.error('Token refresh error:', error);
      throw error;
    }
  }

  /**
   * Ensure token is valid - refresh if needed
   */
  static async ensureValidToken(apiBaseUrl) {
    const accessToken = this.getAccessToken();
    
    if (!accessToken) {
      return false; // No token, user not logged in
    }

    if (this.isTokenExpired(accessToken)) {
      // Expired - try to refresh
      try {
        await this.refreshAccessToken(apiBaseUrl);
        return true;
      } catch (error) {
        this.clear();
        return false;
      }
    }

    if (this.shouldRefreshToken(accessToken)) {
      // About to expire - refresh proactively
      try {
        await this.refreshAccessToken(apiBaseUrl);
      } catch (error) {
        console.warn('Proactive token refresh failed:', error);
        // Continue with current token - will refresh on next check
      }
    }

    return true;
  }

  /**
   * Logout - revoke all tokens
   */
  static async logout(apiBaseUrl) {
    try {
      const accessToken = this.getAccessToken();
      
      if (accessToken) {
        // Notify server to revoke tokens
        await fetch(`${apiBaseUrl}/auth/logout`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${accessToken}`
          }
        }).catch(error => {
          // Log but don't fail - clear tokens locally anyway
          console.error('Server logout failed:', error);
        });
      }
    } finally {
      this.clear();
    }
  }

  /**
   * Clear all tokens and cleanup
   */
  static clear() {
    localStorage.removeItem(this.STORAGE_KEY);
    this.stopRefreshTimer();
  }

  /**
   * Get Authorization header
   */
  static getAuthHeader() {
    const token = this.getAccessToken();
    if (!token) return null;

    return {
      'Authorization': `Bearer ${token}`
    };
  }

  /**
   * Verify token on server (introspect)
   */
  static async verifyToken(token, apiBaseUrl) {
    try {
      const response = await fetch(`${apiBaseUrl}/auth/introspect`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ token })
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Token verification error:', error);
      return { active: false };
    }
  }
}

export default TokenManager;
